import { Select } from '@/components/ui/Select'
import { useAuthStore } from '@/store/auth'
import { useRouteStore } from '@/store/route'
import { userService } from '@services'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

interface VehicleFormData {
  vehicle: string
}

export const VehicleSelect = () => {
  const user = useAuthStore((state) => state.user)
  const updateUserCharityVehicleId = useAuthStore(
    (state) => state.updateUserCharityVehicleId
  )
  const vehicles = useRouteStore((state) => state.vehicles)
  const selectedVehicleId = useRouteStore((state) => state.selectedVehicleId)
  const setSelectedVehicleId = useRouteStore(
    (state) => state.setSelectedVehicleId
  )

  const options = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return []
    return vehicles.map((vehicle) => ({
      text: vehicle.name || `Vehicle ${vehicle.id}`,
      value: String(vehicle.id),
    }))
  }, [vehicles])

  const methods = useForm<VehicleFormData>({
    defaultValues: {
      vehicle: selectedVehicleId ? String(selectedVehicleId) : '',
    },
  })

  const { reset } = methods
  const hasInitialSynced = useRef(false)

  const syncToApi = useCallback(
    (vehicleId: number) => {
      if (!user?.id || user.charity_vehicle_id === vehicleId) return

      userService
        .update(user.id, { charity_vehicle_id: vehicleId })
        .then(() => {
          updateUserCharityVehicleId(vehicleId)
        })
        .catch((error) => {
          console.error('Failed to update charity_vehicle_id:', error)
        })
    },
    [user, updateUserCharityVehicleId]
  )

  // Initialize form, store, and API when options load
  useEffect(() => {
    const value = selectedVehicleId
      ? String(selectedVehicleId)
      : options[0]?.value || ''

    if (!value) return

    reset({ vehicle: value })

    // If defaulting to first option, also update store
    if (!selectedVehicleId && options[0]?.value) {
      setSelectedVehicleId(Number(options[0].value))
    }

    // Sync to API only on initial load
    if (!hasInitialSynced.current) {
      hasInitialSynced.current = true
      syncToApi(Number(value))
    }
  }, [selectedVehicleId, options, reset, setSelectedVehicleId, syncToApi])

  const handleChange = useCallback(
    (value: string | string[]) => {
      const vehicleId = Number(value as string)
      setSelectedVehicleId(vehicleId)
      syncToApi(vehicleId)
    },
    [setSelectedVehicleId, syncToApi]
  )

  return (
    <FormProvider {...methods}>
      <Select<VehicleFormData>
        name="vehicle"
        label="Truck"
        options={options}
        modalTitle="Select Truck"
        onChange={handleChange}
        testId="vehicle-select"
      />
    </FormProvider>
  )
}
